import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { usePostHog } from 'posthog-js/react';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useDomain } from '../../hooks/useDomain';
import { useLogger } from '../../services/logging/logger-context';
import { CreatePhoneCallRequest, retellApiService } from '../../services/retell.api';
import { i18n } from '../../services/translation/i18n';
import { styles } from '../../styles/styles.util';
import { CallbackConfirmationState } from '../../types/callback-confirmation-state.interface';
import { PhoneValidationResult, validatePhoneNumber } from '../../util/phone-validation';
import CustomTextField from '../react-native-material-wrapper/text-field.component';
import { BackButtonHeader } from '../ui-components/back-button-header.component';
import { getTheme } from '../../stores/selectors/theme.selector';
import { useSelector } from 'react-redux';
import { ColorType } from '../../assets/colors/colors.g';
import { ROUTES } from '../../types/routes.enum';

const CallbackConfirmationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const { domain } = useDomain();
  const auth = useAuth();
  const theme = useSelector(getTheme);
  const { logger } = useLogger();
  const retellPosthogFeatureFlag = posthog && posthog.isFeatureEnabled('retell-callback-support');

  // Get parameters from URL or location state with type safety
  const state =
    (location.state as CallbackConfirmationState | null | undefined) ||
    ({} as Partial<CallbackConfirmationState>);
  const publicKey = state.publicKey || '';
  const agentId = state.agentId || '';
  const fromPhoneNumber = state.fromPhoneNumber || '';
  const initialPhoneNumber = state.initialPhoneNumber || '';
  const customerName = state.customerName || '';
  const chargerId = state.chargerId || '';
  const chargerName = state.chargerName || '';
  const chargerSupportPhoneNumber = state.chargerSupportPhoneNumber || '';
  const dynamicVariables = state.dynamicVariables || {};

  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidationResult>(
    validatePhoneNumber(initialPhoneNumber, { required: true }),
  );
  const loggedInUserName = auth.isLoggedIn
    ? `${auth.user?.attributes?.given_name || ''} ${
        auth.user?.attributes?.family_name || ''
      }`.trim()
    : '';

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhone = e.target.value;
    setPhoneNumber(newPhone);
    setPhoneValidation(validatePhoneNumber(newPhone, { required: true }));
  };

  const handleConfirmCallback = async () => {
    if (!phoneValidation.isValid) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const request: CreatePhoneCallRequest = {
        from_number: fromPhoneNumber,
        to_number: phoneValidation.normalized, // Use normalized phone number
        override_agent_id: agentId,
        metadata: {
          customer_name: loggedInUserName || customerName || undefined,
          customer_phone: phoneValidation.normalized,
          // Whether or not the user has decided to use a different phone number,
          // we pass along the number from their account for Zendesk lookup
          customer_phone_from_account: auth.user?.attributes?.phone_number,
          widget_type: 'callback',
          customer_email: auth.user?.attributes?.email || undefined,
          user_id: auth.user?.attributes?.sub || undefined,
          charger_id: chargerId || undefined,
          charger_name: chargerName || undefined,
        },
        retell_llm_dynamic_variables: {
          ...dynamicVariables,
          phone_for_callback: phoneValidation.normalized,
        },
      };

      await retellApiService.createPhoneCall(request, publicKey);

      if (posthog) {
        posthog.capture('retell_callback_requested', {
          customer_phone: phoneValidation.normalized,
          customer_name: loggedInUserName || customerName,
          customer_email: auth.user?.attributes?.email,
          is_logged_in: auth.isLoggedIn,
          domain,
        });
      }

      navigate(`/${chargerName}`, {
        state: { callbackSuccess: true },
      });
    } catch (err) {
      logger.error('Failed to request callback', err);
      setError(i18n.t('callback_error_message'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    if (!isLoading) {
      navigate(-1);
    }
  };

  const handleCallSupport = () => {
    if (posthog) {
      // Capture the event when the user bypasses the callback and calls support directly
      posthog.capture('retell_callback_bypassed');
    }
    if (chargerSupportPhoneNumber) {
      window.location.href = `tel:${chargerSupportPhoneNumber}`;
    }
  };

  // If the user is not logged in or the retell-callback-support feature flag is not enabled
  // redirect to the charger page or the login page
  if (!auth.isLoggedIn || !retellPosthogFeatureFlag) {
    if (chargerName) {
      navigate(`/${chargerName}`);
    } else {
      navigate(`/app/${domain}/${ROUTES.LOGIN}`);
    }
    return null;
  }

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        <BackButtonHeader onBackArrowClicked={handleGoBack} />

        <Box
          sx={{
            padding: '24px 24px',
            height: '100%',
          }}
        >
          {/* Header */}
          <Typography
            variant='h4'
            sx={{
              fontFamily: 'Inter',
              fontSize: '20px',
              fontWeight: 600,
              lineHeight: '28px',
              color: ColorType.BLACK,
              mb: 2,
            }}
          >
            {i18n.t('contact_support')}
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
              color: ColorType.BLACK,
              mb: 4,
            }}
          >
            {i18n.t('contact_support_description')}
          </Typography>

          {/* Why a callback section */}
          <Box
            sx={{
              backgroundColor: '#FEF7E6',
              p: 2,
              mb: 4,
              border: '1px solid #F6E05E',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <LightbulbOutlinedIcon
                sx={{
                  color: '#FF8F00',
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '0%',
                    verticalAlign: 'middle',
                    color: '#202223',
                    mb: 1,
                  }}
                >
                  {i18n.t('why_a_callback')}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: ColorType.GREY6,
                }}
              >
                {i18n.t('why_a_callback_description')}
              </Typography>
            </Box>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity='error' sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Phone number section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '24px',
                color: ColorType.BLACK,
                mb: 2,
              }}
            >
              {i18n.t('phone_number')}
            </Typography>
            <CustomTextField
              value={phoneNumber}
              onChange={handlePhoneChange}
              placeholder={i18n.t('enter_phone_number')}
              fullWidth
              disabled={isLoading}
              variant='filled'
              error={!!phoneNumber.trim() && !phoneValidation.isValid}
              helperText={
                !!phoneNumber.trim() && phoneValidation.errorKey
                  ? i18n.t(phoneValidation.errorKey)
                  : undefined
              }
              sx={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '24px',
                color: '#F2F4F6',
                '& .MuiFilledInput-root': {
                  height: '48px',
                },
                '& .MuiFilledInput-input': {
                  padding: '12px 16px',
                },
              }}
              InputProps={{
                disableUnderline: true,
              }}
            />
          </Box>

          {/* Confirm button */}
          <Button
            variant='contained'
            onClick={handleConfirmCallback}
            disabled={isLoading || !phoneNumber?.trim() || !phoneValidation.isValid}
            fullWidth
            sx={{
              backgroundColor: theme.colorPalettes.primary,
              color: ColorType.WHITE,
              fontSize: '16px',
              fontWeight: 600,
              height: '48px',
              borderRadius: '4px',
              textTransform: 'none',
              fontFamily: 'Inter',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: theme.colorPalettes.primary,
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: ColorType.GREY0,
                color: ColorType.GREY3,
              },
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} color='inherit' />
                <span>Requesting callback...</span>
              </Box>
            ) : (
              i18n.t('confirm_callback')
            )}
          </Button>

          {/* Having trouble? Call support directly */}
          {chargerSupportPhoneNumber && (
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  color: ColorType.GREY6,
                }}
              >
                {i18n.t('having_trouble')}
              </Typography>
              <Typography
                onClick={handleCallSupport}
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  color: theme.colorPalettes.primary,
                  cursor: 'pointer',
                }}
              >
                {i18n.t('call_support_directly')}
              </Typography>
            </Box>
          )}
        </Box>
      </div>
    </div>
  );
};

export default CallbackConfirmationPage;
